import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSession } from "../auth/SessionProvider";
import { useMfaGate } from "../auth/mfa";
import { getSupabaseBrowserClient } from "../integrations/supabase/client";
import { recordSecurityEvent } from "../auth/securityEvents";
import { MIN_PASSWORD_LENGTH, weakPasswordMessage } from "../auth/passwordRules";
import { BoxState } from "../ui/BoxState";
import { Callout } from "../ui/Callout";
import { AuthFrame } from "./auth/AuthFrame";
import { CodeStep } from "./auth/CodeStep";
import { PasswordChecklist, PasswordField } from "./auth/PasswordField";

/**
 * Choosing a password when you do not have one, or do not remember the one you had.
 *
 * Two routes render this: `/invite` for somebody Petrovic has just given access to, and
 * `/new-password` for somebody who asked for a reset. The mechanism is identical and only the
 * sentences differ, so it is one component rather than two files that would have to be kept saying
 * the same thing.
 *
 * **There is no current-password field here, and that is not a weaker control than the one in
 * account settings.** The proof is a different proof: a single-use token that GoTrue mailed to an
 * address it already holds for this account, and that this browser has just exchanged for a session.
 * Asking for the current password as well would ask for the one thing the person provably does not
 * have, which is what brought them here.
 *
 * **A reset signs every other session out.** The reason a person resets a password is often that
 * somebody else might have had it, and a reset that leaves the other session alive answers the
 * inconvenience and not the threat. An invitation does not do this, because there is nothing to
 * revoke: the account has never been signed in anywhere.
 */

/**
 * GoTrue's refusal of a new password at aal1 on an account with a verified factor, as its own sentence.
 *
 * Build round 45b (2026-10-13). "AAL2 session is required to update email or password when MFA is
 * enabled", 401 `insufficient_aal`. The code step below should mean a person never meets it; this is
 * for the one who does (a factor registered in another tab after this page opened), and it must never
 * read as the generic "kunde inte sparas", which is what Mattej read on staging that morning with no way
 * to tell why.
 */
export const NEEDS_CODE_SENTENCE =
  "Kontot har en verifieringsapp, så lösenordet kan bara bytas efter koden från den. Ladda om sidan och ange koden.";

/** The line for the case the code step cannot fix: the app is gone. */
export const LOST_APP_SENTENCE =
  "Har du tappat bort appen? Hör av dig till Petrovic Systems, så hjälper vi dig in igen.";

function needsCode(error: unknown): boolean {
  const candidate = error as { code?: string; status?: number; message?: string } | null;
  return candidate?.code === "insufficient_aal" || /aal2/i.test(candidate?.message ?? "");
}

export interface SetPasswordRouteProps {
  /** Which of the two arrivals this is. It changes the words and whether other sessions are ended. */
  kind: "invite" | "recovery";
}

/**
 * The words per arrival.
 *
 * Changed 2026-09-24 with the client sketch (`prototype/kund/pages-auth.js`): the invitation's lead
 * went, because the heading and the button already say it, and the recovery's lead lost its first
 * half, which repeated the heading word for word. What is left of it is the one fact a person could
 * not guess, that the other devices are signed out, as a quiet line. `null` means no line.
 */
const COPY = {
  invite: {
    heading: "Välj ett lösenord",
    note: null,
    submit: "Skapa lösenord",
    missing:
      "Den här sidan öppnas från länken i inbjudningsmejlet. Öppna länken igen, så hamnar du här med rätt konto."
  },
  recovery: {
    heading: "Välj ett nytt lösenord",
    note: "Du loggas ut från alla andra enheter när det är klart.",
    submit: "Spara lösenord",
    missing:
      "Den här sidan öppnas från länken i återställningsmejlet. Länken gäller en timme och går att använda en gång, så begär en ny om det var längesedan."
  }
} as const;

export function SetPasswordRoute({ kind }: SetPasswordRouteProps) {
  const { status, signOut } = useSession();
  const gate = useMfaGate();
  const navigate = useNavigate();
  const copy = COPY[kind];

  const [next, setNext] = useState("");
  const [repeat, setRepeat] = useState("");
  const [revealed, setRevealed] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  // The too-short alert that stood here went on 2026-09-24: the checklist under the field says the
  // same thing while the person types, and an alert from the first character to the twelfth was
  // the loudest line on the page for the most ordinary state of it.
  const mismatch = repeat.length > 0 && next !== repeat;
  const ready = next.length >= MIN_PASSWORD_LENGTH && next === repeat;

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!ready) {
      return;
    }

    setPending(true);
    setFailure(null);

    try {
      const supabase = getSupabaseBrowserClient();
      const { error } = await supabase.auth.updateUser({ password: next });

      if (error) {
        // No `current_password` here, and that is the whole difference from account settings: a
        // person arriving on this screen provably does not have one. Measured on 2026-09-03 against
        // the staging project, which has `require current password` switched on: a session minted
        // from a recovery or an invitation link sets a password with the new one alone, 200.
        // Corrected 2026-10-13, build round 45b: that measurement was made with an account that had no
        // verified factor. With one, GoTrue refuses at aal1 (401 `insufficient_aal`), which is why the
        // code step below comes first, and why the refusal has a sentence of its own.
        setFailure(
          weakPasswordMessage(error) ??
            (needsCode(error) ? NEEDS_CODE_SENTENCE : "Lösenordet kunde inte sparas. Försök igen.")
        );
        return;
      }

      // A recovery only, since 2026-09-22. Until then the invitation recorded it too, on the
      // reasoning that a person who did not ask for an account should learn one exists. The
      // invitation mail already tells them that, and the notice made "Lösenordet har bytts, var det
      // inte ni, hör av er direkt" the first thing a new client read, about a password they had
      // chosen four seconds earlier. A recovery is the case plan.md section 5.7 names outright. The
      // notice is written before the sign-out below, because that call ends every other session and
      // the write has to happen while this one is still the caller.
      if (kind === "recovery") {
        void recordSecurityEvent("password_changed");
      }

      if (kind === "recovery") {
        // `others` and not `global`. This session is the one that just proved itself through the
        // mailed link, and ending it here would drop the person back on the login screen holding a
        // password they set four seconds ago.
        await signOut("others");
      }

      navigate("/overview", { replace: true });
    } finally {
      setPending(false);
    }
  }

  if (status === "loading") {
    return (
      <AuthFrame>
        <BoxState kind="loading" title="Öppnar portalen" />
      </AuthFrame>
    );
  }

  /**
   * No session means the link was never exchanged, so there is nobody to set a password for.
   *
   * Rendering the form anyway would collect a password and then fail on the save, which reads as a
   * broken portal rather than as an expired link.
   */
  if (status === "signed-out") {
    return (
      <AuthFrame titleId="set-password-heading">
        <h2 id="set-password-heading" className="k-auth-title">
          {copy.heading}
        </h2>
        <p data-testid="set-password-no-session">{copy.missing}</p>
        <Link className="button is-primary is-block" to={kind === "recovery" ? "/recover" : "/login"}>
          <span>{kind === "recovery" ? "Begär en ny länk" : "Till inloggningen"}</span>
        </Link>
      </AuthFrame>
    );
  }

  if (gate.isLoading) {
    return (
      <AuthFrame>
        <BoxState kind="loading" title="Öppnar portalen" />
      </AuthFrame>
    );
  }

  /**
   * The code first, when the account has a verified factor and this session is still aal1.
   *
   * Build round 45b (2026-10-13). A recovery or invitation link mints an aal1 session, and GoTrue
   * refuses a new password at aal1 on an account with a verified factor. So nobody with an
   * authenticator, no admin and no client, could reset a forgotten password. The same step as the
   * sign-in's, with its words; once the code is accepted the token is aal2, the gate answers
   * `satisfied` and the form below appears. An invitation's account has no factor yet, so it never
   * stops here.
   */
  if (gate.data?.kind === "challenge-required") {
    return (
      <CodeStep
        factorId={gate.data.factorId}
        hint="Kontot har en verifieringsapp. Ange koden innan du väljer lösenord."
        cancel={{ label: "Avbryt och logga ut", onClick: () => void signOut("local") }}
        onVerified={() => undefined}
        footnote={
          <p className="k-auth-note" data-testid="set-password-lost-app">
            {LOST_APP_SENTENCE}
          </p>
        }
      />
    );
  }

  /**
   * Drawn after `setPasswordPage` in `prototype/kund/pages-auth.js`, approved 2026-09-24. The rules
   * are a list under the field that ticks as the person types, in place of the sentence
   * `PASSWORD_HINT` that listed them. The list gates nothing: the button waits for the length and
   * the repeat as it did, and the server decides the rest.
   */
  return (
    <AuthFrame titleId="set-password-heading">
      <h2 id="set-password-heading" className="k-auth-title">
        {copy.heading}
      </h2>
      {copy.note ? <p className="k-auth-note">{copy.note}</p> : null}

      <form onSubmit={onSubmit} noValidate>
        <PasswordField
          id="new-password"
          label="Lösenord"
          autoComplete="new-password"
          minLength={MIN_PASSWORD_LENGTH}
          value={next}
          disabled={pending}
          onChange={setNext}
          revealed={revealed}
          onToggle={() => setRevealed((value) => !value)}
          describedBy="new-password-rules"
        />

        <PasswordChecklist id="new-password-rules" password={next} />

        <div className="box-field">
          <label htmlFor="repeat-password">Upprepa lösenordet</label>
          <input
            id="repeat-password"
            type="password"
            autoComplete="new-password"
            required
            value={repeat}
            disabled={pending}
            onChange={(event) => setRepeat(event.target.value)}
          />
        </div>

        {mismatch ? (
          <Callout tone="danger" role="alert" testId="set-password-mismatch">
            De två fälten är inte lika.
          </Callout>
        ) : null}

        {failure ? (
          <Callout tone="danger" role="alert" testId="set-password-error">
            {failure}
          </Callout>
        ) : null}

        <button className="button is-primary is-block" type="submit" disabled={!ready || pending}>
          <span>{pending ? "Sparar" : copy.submit}</span>
        </button>
      </form>
    </AuthFrame>
  );
}

/** `/invite` and `/new-password` differ in words and in one call, so they are one component. */
/** `/invite` and `/new-password` differ in words and in one call, so they are one component. */
export function InviteRoute() {
  return <SetPasswordRoute kind="invite" />;
}

export function NewPasswordRoute() {
  return <SetPasswordRoute kind="recovery" />;
}
